import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Award, Briefcase, Clock, DollarSign, Heart, MapPin, TrendingUp, Users } from 'lucide-react';

const Careers = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const jobCategories = [
    { id: 'all', name: 'All roles' },
    { id: 'finance', name: 'Finance' },
    { id: 'operations', name: 'Operations' },
    { id: 'technology', name: 'Technology' }
  ];

  const jobs = [
    {
      title: 'Senior investment analyst',
      department: 'Finance',
      category: 'finance',
      location: 'Accra, Ghana',
      type: 'Full-time',
      experience: '5+ years',
      salary: 'Competitive'
    },
    {
      title: 'Operations manager',
      department: 'Operations',
      category: 'operations',
      location: 'Aburi, Ghana',
      type: 'Full-time',
      experience: '7+ years',
      salary: 'Competitive'
    },
    {
      title: 'Product engineer',
      department: 'Technology',
      category: 'technology',
      location: 'Remote',
      type: 'Full-time',
      experience: '3+ years',
      salary: 'Competitive'
    }
  ];

  const values = [
    { icon: Users, title: 'Collaboration', description: 'We believe strong teams make the strongest outcomes.' },
    { icon: Award, title: 'Excellence', description: 'We hold ourselves to a high standard in every engagement.' },
    { icon: Heart, title: 'Integrity', description: 'Trust and transparency are the foundation of our work.' },
    { icon: TrendingUp, title: 'Growth', description: 'We build careers through learning, mentorship, and opportunity.' }
  ];

  const filteredJobs = activeFilter === 'all' ? jobs : jobs.filter((job) => job.category === activeFilter);

  return (
    <div className="min-h-screen bg-[#f8fbff] pt-24">
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Careers</p>
            <h1 className="mt-4 text-4xl font-semibold text-slate-900 sm:text-5xl">Join a team building practical financial progress.</h1>
            <p className="mt-6 text-lg leading-8 text-slate-600">We are looking for thoughtful professionals who want to contribute to meaningful work across finance, operations, and technology.</p>
          </motion.div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-12">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Culture</p>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">A calm, collaborative environment that values people.</h2>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {values.map((value, index) => (
              <motion.div key={value.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.06 }} className="rounded-2xl border border-blue-100 bg-blue-50/70 p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <value.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{value.title}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-12">
          <div className="flex flex-wrap gap-3">
            {jobCategories.map((category) => (
              <button key={category.id} onClick={() => setActiveFilter(category.id)} className={`rounded-full px-4 py-2 text-sm font-semibold transition ${activeFilter === category.id ? 'bg-blue-600 text-white' : 'bg-blue-50/70 text-slate-700'}`}>
                {category.name}
              </button>
            ))}
          </div>

          <div className="mt-8 space-y-4">
            {filteredJobs.map((job) => (
              <div key={job.title} className="rounded-2xl border border-blue-100 bg-blue-50/70 p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900">{job.title}</h3>
                    <p className="mt-1 text-sm font-medium text-blue-600">{job.department}</p>
                  </div>
                  <div className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-slate-700">{job.type}</div>
                </div>
                <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-600">
                  <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{job.location}</span>
                  <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{job.experience}</span>
                  <span className="flex items-center gap-2"><DollarSign className="h-4 w-4" />{job.salary}</span>
                </div>
                <div className="mt-5 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Briefcase className="h-4 w-4 text-blue-600" />
                  Interested? Reach out and we will share the next steps.
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
